import {
  isAdminRole as rolesAreAdmin,
  isRegularUserRole,
  isSuperAdminRole
} from '@/utils/systemRoles';

/**
 * @typedef {Object} ShellAuthContext
 * @property {(code: string) => boolean} hasPermission
 * @property {boolean} isSuperAdmin
 * @property {boolean} isAdminRole
 * @property {boolean} isRegularUser
 */

/**
 * Shell tab visibility (mirrors router guard intent).
 * Permission-based; only super_admin bypasses missing grants.
 * @param {object} tab
 * @param {ShellAuthContext} ctx
 */
export function canSeeShellTab(tab, ctx) {
  if (!tab) return false;

  if (tab.hidden) {
    return false;
  }

  if (tab.isPlaceholder) {
    return true;
  }

  if (tab.key === 'table' && ctx.isRegularUser) {
    return false;
  }

  if (tab.visibleForRegularUser && ctx.isRegularUser) {
    return true;
  }

  const required = tab.requiredAnyPermission;
  if (!required?.length) {
    return true;
  }

  if (required.some((code) => ctx.hasPermission(code))) {
    return true;
  }

  if (ctx.isSuperAdmin) {
    return true;
  }

  // No dept_admin role bypass — permissions from /me are source of truth.
  return false;
}

/**
 * Admin sidebar tab visibility — permission-based; super_admin bypass only.
 * @param {object} tab
 * @param {ShellAuthContext} ctx
 */
export function canSeeShellAdminTab(tab, ctx) {
  if (!tab || tab.hidden) return false;

  const required = tab.permissionAny;
  if (!required?.length) return true;

  if (required.some((code) => ctx.hasPermission(code))) {
    return true;
  }

  if (ctx.isSuperAdmin) return true;

  return false;
}

/**
 * Build auth context for shell visibility helpers from useAuth().
 * @param {{ hasPermission: Function, hasRole: Function, isSuperAdmin: import('vue').Ref<boolean>|boolean, userRoles?: import('vue').Ref|Array }} auth
 */
export function buildShellAuthContext(auth) {
  const isSuperAdmin =
    typeof auth.isSuperAdmin === 'object' && auth.isSuperAdmin !== null
      ? !!auth.isSuperAdmin.value
      : !!auth.isSuperAdmin;

  const rolesRaw =
    auth.userRoles && typeof auth.userRoles === 'object' && 'value' in auth.userRoles
      ? auth.userRoles.value
      : auth.userRoles;

  let roles = Array.isArray(rolesRaw) ? rolesRaw : [];
  if (!roles.length && typeof auth.hasRole === 'function') {
    // Fallback when caller only exposes hasRole
    const codes = ['super_admin', 'dept_admin', 'dept_user', 'user'];
    roles = codes.filter((c) => auth.hasRole(c));
  }

  const isAdminRole = rolesAreAdmin(roles) || isSuperAdmin;
  const isRegularUser = isRegularUserRole(roles);

  return {
    hasPermission: auth.hasPermission,
    isSuperAdmin: isSuperAdmin || isSuperAdminRole(roles),
    isAdminRole,
    isRegularUser
  };
}
